<script>
import { mapActions } from "vuex";
import QRCode from "qrcode";
import TrendChart from "@/components/referral/TrendChart.vue";

export default {
  name: "AdminCallMarkazShow",
  components: { TrendChart },

  data: () => ({
    id: null,
    manager: null,
    stat: null,
    series: [],
    students: [],
    loading: false,
    preset: "30",
    from: "",
    to: "",
    copied: false,
    pwCopied: false,
    deskCopied: false,
    qr: "",
    animConv: 0,
  }),

  computed: {
    referralLink() {
      return this.id ? `https://qabul.usat.uz/?manager_id=${this.id}` : "";
    },
    // Staff-desk (/admission) variant of the same manager link — opens the desk already attributed
    // to this manager.
    deskLink() {
      return this.id ? `https://qabul.usat.uz/admission/?manager_id=${this.id}` : "";
    },
    conversion() {
      return Math.max(0, Math.min(100, Number(this.stat?.conversion) || 0));
    },
    dash() {
      const C = 2 * Math.PI * 52;
      return { C, offset: C * (1 - this.animConv / 100) };
    },
    bestDay() {
      if (!this.series.length) return null;
      return this.series.reduce((a, b) =>
        (Number(b.applicants) || 0) > (Number(a.applicants) || 0) ? b : a
      );
    },
  },

  watch: {
    referralLink: "renderQr",
  },

  async mounted() {
    this.id = this.$route.params.id;
    this.setPreset("30", false);
    this.renderQr();
    await this.loadManager();
    await this.reloadPeriod();
  },

  methods: {
    ...mapActions("admin-call-markaz", [
      "MANAGER_BY_ID",
      "MANAGER_STAT_BY_ID",
      "MANAGER_TIMESERIES",
      "MANAGER_STUDENTS",
    ]),

    async loadManager() {
      this.manager = await this.MANAGER_BY_ID({ id: this.id });
      this.renderQr();
    },

    async reloadPeriod() {
      this.loading = true;
      try {
        const [stat, series, students] = await Promise.all([
          this.MANAGER_STAT_BY_ID({ id: this.id, from: this.from, to: this.to }),
          this.MANAGER_TIMESERIES({ id: this.id, from: this.from, to: this.to }),
          this.MANAGER_STUDENTS({ id: this.id, from: this.from, to: this.to }),
        ]);
        this.stat = stat;
        this.series = Array.isArray(series) ? series : [];
        this.students = Array.isArray(students) ? students : [];
        this.animateConversion();
      } finally {
        this.loading = false;
      }
    },

    animateConversion() {
      const target = this.conversion;
      const dur = 850;
      const start = Date.now();
      const from = this.animConv;
      const tick = () => {
        const t = Math.min(1, (Date.now() - start) / dur);
        const e = 1 - Math.pow(1 - t, 3);
        this.animConv = from + (target - from) * e;
        if (t < 1) requestAnimationFrame(tick);
        else this.animConv = target;
      };
      requestAnimationFrame(tick);
    },

    isoDaysAgo(n) {
      const d = new Date();
      d.setDate(d.getDate() - n);
      return d.toISOString().slice(0, 10);
    },
    todayIso() {
      return new Date().toISOString().slice(0, 10);
    },

    setPreset(key, reload = true) {
      this.preset = key;
      if (key === "all") {
        this.from = "";
        this.to = "";
      } else if (key === "custom") {
        // keep whatever is in the inputs
      } else {
        this.from = this.isoDaysAgo(Number(key) - 1);
        this.to = this.todayIso();
      }
      if (reload) this.reloadPeriod();
    },

    applyCustom() {
      this.preset = "custom";
      this.reloadPeriod();
    },

    async renderQr() {
      if (!this.referralLink) {
        this.qr = "";
        return;
      }
      try {
        this.qr = await QRCode.toDataURL(this.referralLink, {
          margin: 1,
          width: 320,
          color: { dark: "#0c1322ff", light: "#ffffffff" },
        });
      } catch {
        this.qr = "";
      }
    },

    async copyLink() {
      const link = this.referralLink;
      if (!link) return;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    },

    async copyDeskLink() {
      const link = this.deskLink;
      if (!link) return;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.deskCopied = true;
      setTimeout(() => (this.deskCopied = false), 1500);
    },

    async copyPassword() {
      const pw = this.manager && this.manager.password;
      if (!pw) return;
      try {
        await navigator.clipboard.writeText(pw);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = pw;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.pwCopied = true;
      setTimeout(() => (this.pwCopied = false), 1500);
    },


    fmtPct(v) {
      const n = Number(v) || 0;
      return `${n.toFixed(n % 1 === 0 ? 0 : 1)}%`;
    },
    fmtDate(d) {
      if (!d) return "";
      return new Date(d + "T00:00:00Z").toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    },
    fmtFull(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "—";
      return dt.toLocaleDateString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    fmtSum(n) {
      const v = Number(n);
      if (!v) return "";
      return v.toLocaleString("ru-RU") + " so'm";
    },
    statusMeta(s) {
      const map = {
        DRAFT: { label: "Qoralama", color: "#6b7891" },
        SUBMITTED: { label: "Topshirilgan", color: "#38bdf8" },
        EXAMINED: { label: "Imtihon topshirgan", color: "#38bdf8" },
        ACCEPTED: { label: "Qabul qilingan", color: "#34d399" },
        PASSED: { label: "O'tgan", color: "#34d399" },
        FAILED: { label: "O'tmagan", color: "#f87171" },
        REJECTED: { label: "Rad etilgan", color: "#f87171" },
      };
      return map[s] || { label: s || "—", color: "#6b7891" };
    },
    statusLabel(s) {
      return this.statusMeta(s).label;
    },
    statusStyle(s) {
      const c = this.statusMeta(s).color;
      return { color: c, background: c + "22", borderColor: c + "55" };
    },
    goBack() {
      this.$router.push("/call-markaz-2026-2027");
    },
    // Row click → open that applicant's profile. s.id IS the application id (ReferredStudentDTO.id).
    goToApplication(id) {
      if (id) this.$router.push(`/application-show/${id}`);
    },
  },
};
</script>

<template>
  <v-container fluid class="rmx">
    <!-- BACK -->
    <v-btn
      @click="goBack"
      elevation="0"
      rounded
      large
      class="font-made modern-back-btn mb-6 px-6 py-3 d-flex align-center"
      style="
        border: 1px solid #e0e0e0;
        background: linear-gradient(135deg, #ffffff, #f9f9f9);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
        transition: all 0.25s ease;
      "
    >
      <v-icon
        left
        size="22"
        style="color:#0c0c0c; transition: transform 0.2s;"
        class="back-icon"
      >
        mdi-arrow-left
      </v-icon>
      <span
        class="font-weight-bold text-uppercase"
        style="letter-spacing:1px; font-size:14px; color:#0c0c0c;"
      >
        {{ $t("Back") }}
      </span>
    </v-btn>

    <!-- HERO -->
    <header class="rmx-hero">
      <div class="rmx-hero-id" v-if="manager">
        <div class="rmx-hero-meta">
          <div class="rmx-eyebrow">{{ $t('Manager') }}</div>
          <h1 class="rmx-hero-name">{{ manager.name }}</h1>
          <div class="rmx-hero-contact" v-if="manager.position">
            <span>{{ manager.position }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- PERIOD -->
    <div class="rmx-period">
      <div class="rmx-presets">
        <button :class="{ on: preset === '7' }" @click="setPreset('7')">7{{ $t('d') }}</button>
        <button :class="{ on: preset === '30' }" @click="setPreset('30')">30{{ $t('d') }}</button>
        <button :class="{ on: preset === '90' }" @click="setPreset('90')">90{{ $t('d') }}</button>
        <button :class="{ on: preset === 'all' }" @click="setPreset('all')">{{ $t('All') }}</button>
      </div>
      <div class="rmx-custom">
        <input type="date" v-model="from" @change="applyCustom" />
        <span class="rmx-dash">—</span>
        <input type="date" v-model="to" @change="applyCustom" />
      </div>
    </div>

    <!-- TOP GRID: share + donut -->
    <div class="rmx-grid-top">
      <section class="rmx-card rmx-share">
        <div class="rmx-card-eyebrow">{{ $t('Referral link') }}</div>
        <div class="rmx-share-body">
          <div class="rmx-qr">
            <img v-if="qr" :src="qr" :alt="$t('Referral link')" />
          </div>
          <div class="rmx-share-info">
            <code class="rmx-share-link">{{ referralLink }}</code>
            <button class="rmx-share-copy" @click="copyLink">
              <v-icon left size="16">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              {{ copied ? $t('Copied') : $t('Copy link') }}
            </button>
            <p class="rmx-share-hint">{{ $t('Each manager has a personal link; students who register through it are assigned to that manager in Bitrix.') }}</p>

            <!-- /admission staff-desk link (same attribution, opens the desk directly) -->
            <div class="rmx-card-eyebrow" style="margin-top: 14px;">{{ $t('Desk link') }}</div>
            <a
              :href="deskLink"
              target="_blank"
              rel="noopener noreferrer"
              class="rmx-share-link"
              style="display: block; color: #00274c; text-decoration: underline; word-break: break-all;"
            >{{ deskLink }}</a>
            <button class="rmx-share-copy" @click="copyDeskLink" style="margin-top: 8px;">
              <v-icon left size="16">{{ deskCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              {{ deskCopied ? $t('Copied') : $t('Copy link') }}
            </button>

            <!-- /admission desk password -->
            <div class="rmx-card-eyebrow" style="margin-top: 18px;">{{ $t('Admission desk password') }}</div>
            <code class="rmx-share-link">{{ (manager && manager.password) || '—' }}</code>
            <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px;">
              <button class="rmx-share-copy" @click="copyPassword">
                <v-icon left size="16">{{ pwCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                {{ pwCopied ? $t('Copied') : $t('Copy') }}
              </button>
            </div>
            <p class="rmx-share-hint">{{ $t('The staffer logs in at the /admission desk with this password.') }}</p>
          </div>
        </div>
      </section>

      <section class="rmx-card rmx-donut-card">
        <div class="rmx-card-eyebrow">{{ $t('Conversion') }}</div>
        <div class="rmx-donut">
          <svg viewBox="0 0 120 120">
            <defs>
              <linearGradient id="rmxRing" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="var(--rmx-gold)" />
                <stop offset="100%" stop-color="var(--rmx-emerald)" />
              </linearGradient>
            </defs>
            <circle class="rmx-ring-track" cx="60" cy="60" r="52" />
            <circle
              class="rmx-ring-val"
              cx="60"
              cy="60"
              r="52"
              :stroke-dasharray="dash.C"
              :stroke-dashoffset="dash.offset"
              transform="rotate(-90 60 60)"
            />
          </svg>
          <div class="rmx-donut-center">
            <span class="rmx-donut-pct">{{ fmtPct(animConv) }}</span>
            <span class="rmx-donut-sub">{{ stat ? stat.reachedContract : 0 }} / {{ stat ? stat.applicants : 0 }}</span>
          </div>
        </div>
      </section>
    </div>

    <!-- KPI CARDS -->
    <div class="rmx-grid-kpi">
      <div class="rmx-card rmx-stat rmx-stat--applicants">
        <div class="rmx-stat-top">
          <span class="rmx-stat-label">{{ $t('Applicants') }}</span>
          <v-icon size="18">mdi-account-multiple</v-icon>
        </div>
        <div class="rmx-stat-num">{{ stat ? stat.applicants : 0 }}</div>
      </div>
      <div class="rmx-card rmx-stat rmx-stat--applications">
        <div class="rmx-stat-top">
          <span class="rmx-stat-label">{{ $t('Applications') }}</span>
          <v-icon size="18">mdi-file-document-outline</v-icon>
        </div>
        <div class="rmx-stat-num">{{ stat ? stat.applications : 0 }}</div>
      </div>
      <div class="rmx-card rmx-stat rmx-stat--contracts">
        <div class="rmx-stat-top">
          <span class="rmx-stat-label">{{ $t('Contracts') }}</span>
          <v-icon size="18">mdi-handshake-outline</v-icon>
        </div>
        <div class="rmx-stat-num">{{ stat ? stat.reachedContract : 0 }}</div>
      </div>
      <div class="rmx-card rmx-stat rmx-stat--best">
        <div class="rmx-stat-top">
          <span class="rmx-stat-label">{{ $t('Best day') }}</span>
          <v-icon size="18">mdi-star-outline</v-icon>
        </div>
        <div class="rmx-stat-num">{{ bestDay ? bestDay.applicants : 0 }}</div>
        <div class="rmx-stat-foot">{{ bestDay ? fmtDate(bestDay.date) : '—' }}</div>
      </div>
    </div>

    <!-- TREND -->
    <section class="rmx-card rmx-trend">
      <div class="rmx-trend-head">
        <div class="rmx-card-eyebrow">{{ $t('Acquisition trend') }}</div>
      </div>
      <trend-chart :points="series" :height="260" />
    </section>

    <!-- REFERRED STUDENTS -->
    <section class="rmx-card rmx-students">
      <div class="rmx-students-head">
        <div class="rmx-card-eyebrow">{{ $t('Referred students') }}</div>
        <span class="rmx-students-count">{{ students.length }}</span>
      </div>

      <div v-if="loading" class="rmx-students-empty">{{ $t('Loading...') }}</div>
      <div v-else-if="!students.length" class="rmx-students-empty">
        {{ $t('No referred students in this period') }}
      </div>

      <div v-else class="rmx-tablewrap">
        <table class="rmx-table">
          <thead>
            <tr>
              <th class="rmx-col-num">#</th>
              <th>{{ $t('Full name') }}</th>
              <th>{{ $t('Phone') }}</th>
              <th>{{ $t('Registered') }}</th>
              <th>{{ $t('Filled by') }}</th>
              <th>{{ $t('Status') }}</th>
              <th>{{ $t('Direction') }}</th>
              <th>{{ $t('Contract') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(s, i) in students"
              :key="s.id"
              class="rmx-student-row"
              @click="goToApplication(s.id)"
            >
              <td class="rmx-col-num">{{ i + 1 }}</td>
              <td class="rmx-col-name">{{ s.fullName || '—' }}</td>
              <td>{{ s.phone || '—' }}</td>
              <td>{{ fmtFull(s.registeredDate) }}</td>
              <td>{{ s.filledBy || $t('The student themselves') }}</td>
              <td>
                <span class="rmx-badge" :style="statusStyle(s.status)">{{ statusLabel(s.status) }}</span>
              </td>
              <td class="rmx-col-faculty">{{ s.faculty || '—' }}</td>
              <td>
                <template v-if="s.contractNumber">
                  <span class="rmx-contract-no">{{ s.contractNumber }}</span>
                  <span v-if="s.contractSum" class="rmx-contract-sum">{{ fmtSum(s.contractSum) }}</span>
                </template>
                <span v-else class="rmx-dim">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </v-container>
</template>

<style lang="scss" scoped>
@import "@/components/referral/referral-profile.scss";

/* ORQAGA pill — matches the qabul-xodimlar (referral employee) show page */
.modern-back-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
}
.modern-back-btn:active {
  transform: translateY(0);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}
.back-icon:hover {
  transform: translateX(-3px);
}
/* Clickable applicant rows → open the application-show profile */
.rmx-student-row {
  cursor: pointer;
  transition: background 0.15s ease;
}
.rmx-student-row:hover td {
  background: #f5f7fa;
}
</style>
